import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Save, Upload, ExternalLink } from 'lucide-react';
import { Button } from '../ui/Button';
import { storage, auth, db } from '../../lib/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { ref as dbRef, set, get } from 'firebase/database';
import { toast } from 'sonner';

const APP_URL = 'https://vcard-pro.creagencym.fr/';
const ADMIN_EMAIL = 'mikael.antoine.83@gmail.com';

interface QRCodeStyle {
  fgColor: string;
  bgColor: string;
  style: 'dots' | 'squares' | 'rounded';
}

interface QRCodeSettings {
  style: QRCodeStyle;
  logo: string | null;
  imageUrl: string;
  timestamp: number;
}

interface MarketingQRCodeProps {
  onSave?: (url: string) => void;
}

export function MarketingQRCode({ onSave }: MarketingQRCodeProps) {
  const [qrStyle, setQrStyle] = useState<QRCodeStyle>({
    fgColor: '#4F46E5',
    bgColor: '#FFFFFF',
    style: 'dots'
  });

  const [logo, setLogo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSavedUrl, setLastSavedUrl] = useState<string | null>(null);
  
  const isAdmin = auth.currentUser?.email === ADMIN_EMAIL;

  useEffect(() => {
    const loadLastQRCode = async () => {
      try {
        // Charger les paramètres depuis la base de données
        const settingsRef = dbRef(db, 'marketingQRCode/settings');
        const snapshot = await get(settingsRef);
        
        if (snapshot.exists()) {
          const settings: QRCodeSettings = snapshot.val();
          setQrStyle(settings.style);
          setLogo(settings.logo);
          setLastSavedUrl(settings.imageUrl);
        } else {
          // Si pas de paramètres, charger juste la dernière image
          const marketingRef = storageRef(storage, 'marketing');
          const result = await listAll(marketingRef);
          
          if (result.items.length > 0) {
            const sortedItems = result.items.sort((a, b) => b.name.localeCompare(a.name));
            const lastQRUrl = await getDownloadURL(sortedItems[0]);
            setLastSavedUrl(lastQRUrl);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement du dernier QR code:', error);
      }
    };

    loadLastQRCode();
  }, []);
  
  // URL avec tracking simple
  const trackingUrl = `${APP_URL}?utm_source=marketing&utm_medium=qr`;

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const createQRCodeCanvas = async (size: number) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Créer le QR code en SVG avec le style approprié
    const qrContainer = document.createElement('div');
    const qrCode = (
      <QRCodeSVG
        value={trackingUrl}
        size={size}
        level="H"
        fgColor={qrStyle.fgColor}
        bgColor={qrStyle.bgColor}
        {...(qrStyle.style === 'dots' && { dotsOptions: { type: 'dots' } })}
        {...(qrStyle.style === 'rounded' && { cornersSquareOptions: { type: 'extra-rounded' }, dotsOptions: { type: 'rounded' } })}
      />
    );

    // Render le QR code dans le container
    const reactRoot = document.createElement('div');
    qrContainer.appendChild(reactRoot);
    const ReactDOM = await import('react-dom/client');
    const root = ReactDOM.createRoot(reactRoot);
    root.render(qrCode);

    // Attendre que le QR code soit rendu
    await new Promise(resolve => setTimeout(resolve, 100));

    // Convertir le SVG en image
    const svgElement = qrContainer.querySelector('svg');
    if (!svgElement) {
      throw new Error('Could not find QR code SVG element');
    }

    const svgString = new XMLSerializer().serializeToString(svgElement);
    const qrImage = new Image();
    
    await new Promise((resolve, reject) => {
      qrImage.onload = resolve;
      qrImage.onerror = reject;
      qrImage.src = 'data:image/svg+xml;base64,' + btoa(svgString);
    });

    // Dessiner le QR code
    ctx.fillStyle = qrStyle.bgColor;
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(qrImage, 0, 0, size, size);

    // Ajouter le logo si présent
    if (logo) {
      const logoImg = new Image();
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = logo;
      });

      // Calculer la taille du logo (20% du QR code)
      const logoSize = size * 0.2;
      const logoX = (size - logoSize) / 2;
      const logoY = (size - logoSize) / 2;

      // Ajouter un fond blanc pour le logo
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(logoX, logoY, logoSize, logoSize);

      // Dessiner le logo
      ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
    }

    // Nettoyer
    root.unmount();
    qrContainer.remove();

    return canvas;
  };

  const handleSave = async () => {
    if (!isAdmin) {
      toast.error("Vous devez avoir les droits administrateur pour sauvegarder le QR code marketing.");
      return;
    }

    setIsLoading(true);
    try {
      const canvas = await createQRCodeCanvas(1024);

      // Convertir en blob
      const blob = await new Promise<Blob>((resolve) => 
        canvas.toBlob((blob) => resolve(blob!), 'image/png', 1)
      );

      // Sauvegarder dans Firebase Storage
      const filename = `marketing-qr-${Date.now()}.png`;
      const qrStorageRef = storageRef(storage, `marketing/${filename}`);
      await uploadBytes(qrStorageRef, blob);
      const url = await getDownloadURL(qrStorageRef);

      // Sauvegarder les paramètres dans la base de données
      const settings: QRCodeSettings = {
        style: qrStyle,
        logo,
        imageUrl: url,
        timestamp: Date.now()
      };

      const settingsRef = dbRef(db, 'marketingQRCode/settings');
      await set(settingsRef, settings);

      setLastSavedUrl(url);
      toast.success("QR code sauvegardé avec succès !");
      if (onSave) {
        onSave(url);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if ((error as any)?.code === 'storage/unauthorized') {
        toast.error("Erreur : Vous n'avez pas les permissions nécessaires. Contactez l'administrateur.");
      } else {
        toast.error("Une erreur est survenue lors de la sauvegarde du QR code.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const canvas = await createQRCodeCanvas(1024);
      
      // Télécharger l'image
      const link = document.createElement('a');
      link.download = 'vcard-pro-qr.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      toast.error("Une erreur est survenue lors du téléchargement du QR code.");
    }
  };

  return (
    <div className="space-y-6">
      {!isAdmin && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Vous devez avoir les droits administrateur pour sauvegarder le QR code marketing.
              </p>
            </div>
          </div>
        </div>
      )}

      {lastSavedUrl && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-blue-700">
              Dernier QR code sauvegardé
            </p>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(lastSavedUrl, '_blank')}
                className="inline-flex items-center text-blue-700"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Voir
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col items-center space-y-4">
        <div className="relative">
          <QRCodeSVG
            value={trackingUrl}
            size={256}
            level="H"
            fgColor={qrStyle.fgColor}
            bgColor={qrStyle.bgColor}
            {...(qrStyle.style === 'dots' && { dotsOptions: { type: 'dots' } })}
            {...(qrStyle.style === 'rounded' && { cornersSquareOptions: { type: 'extra-rounded' }, dotsOptions: { type: 'rounded' } })}
          />
          {logo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-white p-1 rounded-sm" style={{ width: '20%', height: '20%' }}>
                <img
                  src={logo}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 w-full max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Couleur QR
            </label>
            <input
              type="color"
              value={qrStyle.fgColor}
              onChange={(e) => setQrStyle({ ...qrStyle, fgColor: e.target.value })}
              className="mt-1 block w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Style
            </label>
            <select
              value={qrStyle.style}
              onChange={(e) => setQrStyle({ ...qrStyle, style: e.target.value as 'dots' | 'squares' | 'rounded' })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="dots">Points</option>
              <option value="squares">Carrés</option>
              <option value="rounded">Arrondis</option>
            </select>
          </div>
        </div>

        <div className="w-full max-w-md">
          <label className="block text-sm font-medium text-gray-700">
            Logo
          </label>
          <div className="mt-1 flex items-center">
            <Button
              variant="outline"
              onClick={() => document.getElementById('logo-upload')?.click()}
              className="w-full inline-flex items-center"
            >
              <Upload className="w-4 h-4 mr-2" />
              {logo ? 'Changer le logo' : 'Ajouter un logo'}
            </Button>
            <input
              id="logo-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
          </div>
        </div>

        <div className="flex space-x-4">
          <Button 
            onClick={handleSave} 
            className="inline-flex items-center"
            disabled={isLoading || !isAdmin}
          >
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button onClick={handleDownload} variant="outline" className="inline-flex items-center">
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </Button>
        </div>
      </div>
    </div>
  );
}
